// @snake-case-api Read historical environment JSON and Terraform snapshots at one boundary.
import type { CloudEnvironment } from '@/core/infra_types'
import { newVmDraft, type VmDraft } from './vmDeployment'

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : {}
}

const snake = (key: string) => key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
const camel = (key: string) => key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())

/** Last submitted variables win, then persisted config, then the original setup.
 * Empty images in a failed legacy apply are not a selection: recover the saved pin.
 * No catalog defaults here: reopening must never implicitly upgrade a deployment.
 */
export function vmDraftFromEnvironment(env: CloudEnvironment, requestId: string = crypto.randomUUID(), mode: 'reapply' | 'resume' = 'reapply'): VmDraft {
  const config = env.config
  const original = record(config['onboardingDraft'] ?? config['onboarding_draft'])
  const sources = mode === 'resume' ? [original] : [record(config['vmApplyConfiguration'] ?? config['vm_apply_configuration']), config, original]
  const read = (key: string, aliases: string[] = [], skipEmpty = false): unknown => {
    for (const source of sources) {
      for (const name of [...aliases, key]) {
        for (const spelling of [name, snake(name), camel(name)]) {
          const value = source[spelling]
          if (value !== undefined && value !== null && (!skipEmpty || value !== '')) return value
        }
      }
    }
    return undefined
  }
  const draft = newVmDraft({ id: String(read('hostId') ?? ''), name: env.name,
    status: 'pending', hostType: 'vpn', createdAt: env.createdAt,
    metadata: { cloudProvider: env.cloudProvider } }, requestId)
  // Preserve module defaults for pre-wizard environments rather than enabling new features.
  draft.dfsEnabled = mode === 'resume'
  draft.sshAccessMode = 'legacy'
  // Cleared as a starting point only: the loop below re-seeds this from
  // disk_size_gb whenever the environment records one. What an empty field
  // means once it survives is emptyDiskKeepsCurrent in vmDeployment.ts.
  draft.diskSizeGb = ''
  draft.vmSize = env.cloudProvider === 'azure' ? 'Standard_D2s_v5'
    : env.cloudProvider === 'upcloud' ? '2xCPU-4GB' : 'e2-standard-4'
  const aliases: Partial<Record<keyof VmDraft, string[]>> = {
    imageName: ['currentImage', 'image_name', 'template_name'],
    moduleVersion: ['module_version', 'tf_module_version'],
    vmSize: ['vm_size', 'plan'], dbTier: ['db_sku', 'db_plan'],
    intelligenceDbTier: ['intelligence_db_sku', 'intelligence_db_plan'],
    availabilityZones: ['availability_zones_json'], sshAllowedCidrs: ['ssh_allowed_cidrs_json'],
  }
  for (const key of Object.keys(draft) as (keyof VmDraft)[]) {
    if (['requestId', 'provider', 'region', 'hostId'].includes(key)) continue
    let value = read(key, aliases[key], ['imageName', 'moduleVersion', 'moduleDigest', 'vmSize'].includes(key))
    if (value === undefined) continue
    if (key === 'availabilityZones' || key === 'sshAllowedCidrs') {
      if (typeof value === 'string' && value.trim().startsWith('[')) {
        try { value = JSON.parse(value) } catch { /* Keep malformed input visible to validation. */ }
      }
      if (Array.isArray(value) && value.every((item) => typeof item === 'string')) {
        Object.assign(draft, { [key]: key === 'sshAllowedCidrs' ? value.join(', ') : value })
      } else if (key === 'sshAllowedCidrs' && typeof value === 'string') draft.sshAllowedCidrs = value
    } else if (typeof draft[key] === 'boolean') {
      if ([true, false, 'true', 'false'].includes(value as boolean | string)) {
        Object.assign(draft, { [key]: value === true || value === 'true' })
      }
    } else if (typeof value === 'string' || typeof value === 'number') {
      Object.assign(draft, { [key]: String(value) })
    }
  }
  draft.region = env.region
  return draft
}
